/**
 * The slice of the Elastic APM agent this app drives. The real agent is
 * preloaded by the container entrypoint (`-r elastic-apm-node/start`); naming
 * it as an interface keeps the transaction naming testable without it.
 */
export interface ApmAgent {
	isStarted(): boolean;
	setTransactionName(name: string): void;
}

/** Stands in for a request that resolved to no page. */
const UNRESOLVED_PREFIX = '/*';

const ROOT_PATH = '/';

const CLIENT_ERROR_STATUS = 400;

/**
 * The request path, without the query string: a query carries no routing
 * meaning, so keeping it would split one page across a group per campaign
 * parameter. The full URL, query included, stays on the transaction itself.
 */
export function normalizeTransactionPath(originalUrl: string): string {
	const segments = originalUrl.split('?')[0].split('/').filter(Boolean);

	return segments.length === 0 ? ROOT_PATH : `/${segments.join('/')}`;
}

/**
 * Replaces the agent's `unknown route` fallback, which every page inherits
 * because they are all served by one catch-all handler with no route pattern
 * for the agent to read.
 *
 * A request that resolved to no page collapses into one group per status code.
 * Those paths are crawler- and scanner-controlled, so naming transactions after
 * them would create a group per probe and push the real pages past Elastic's
 * transaction group limit; the exact path stays on the transaction and on every
 * log line for the request.
 */
export function nameApmTransaction(
	agent: ApmAgent | undefined,
	request: { method: string; originalUrl: string },
	statusCode: number,
): void {
	if (!agent?.isStarted()) {
		return;
	}

	const name =
		statusCode >= CLIENT_ERROR_STATUS
			? `${UNRESOLVED_PREFIX}${statusCode}`
			: normalizeTransactionPath(request.originalUrl);

	agent.setTransactionName(`${request.method} ${name}`);
}
